import React, { useState } from "react";
import {
  FaTimes,
  FaCheckCircle,
  FaSpinner,
  FaLayerGroup,
} from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

interface Props {
  isOpen: boolean;
  module: string;
  examId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const CreateTeilModal: React.FC<Props> = ({
  isOpen,
  module,
  examId,
  onClose,
  onSuccess,
}) => {
  const [partNumber, setPartNumber] = useState(1);
  const [partTitle, setPartTitle] = useState("");
  const [instructions, setInstructions] = useState("");
  const [creating, setCreating] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);

    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/exam-parts`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          examSetId: parseInt(examId),
          module,
          partNumber,
          displayOrder: partNumber,
          instructions,
          partTitle: partTitle || null,
        }),
      });

      if (res.ok) {
        onSuccess();
        setPartNumber(1);
        setPartTitle("");
        setInstructions("");
      } else {
        const data = await res.json();
        alert(data.message || "Failed to create Teil");
      }
    } catch (err) {
      alert("Network error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-gradient-to-r from-[#009fe3] to-cyan-500 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FaLayerGroup />
            Create Teil for {module}
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <FaTimes size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Teil Number *
            </label>
            <input
              type="number"
              value={partNumber}
              onChange={(e) => setPartNumber(parseInt(e.target.value) || 1)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              min="1"
              max="10"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Teil Title (Optional)
            </label>
            <input
              type="text"
              value={partTitle}
              onChange={(e) => setPartTitle(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              placeholder="e.g., Informationen verstehen"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Instructions *
            </label>
            <textarea
              value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              rows={4}
              className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#009fe3]"
              placeholder="Sie lesen in einer Zeitung..."
              required
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={creating}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg disabled:opacity-50"
            >
              {creating ? (
                <>
                  <FaSpinner className="animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FaCheckCircle />
                  Create Teil
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={creating}
              className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300 disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
